// ============================================================
// imageActions.ts
// A small TipTap extension that puts a ✕ (remove) button on the currently
// SELECTED inline image, as a clearer alternative to pressing Delete.
//
// The @tiptap/extension-image resize option renders each image through a
// ResizableNodeView whose DOM is
//   div[data-resize-container] > div[data-resize-wrapper] > img + resize-handles
// We DON'T re-implement that node view (that would lose resize + native drag).
// Instead a ProseMirror plugin watches the selection: when a NodeSelection lands
// on an image, it imperatively appends a ✕ button into that image's
// [data-resize-wrapper] (position:relative, so absolute top-right sits over the
// image corner) and removes it when the image is deselected. Clicking ✕ deletes
// the selected image node. This composes with resize (corner handles) and native
// drag-to-reposition — all three coexist. Frontend-only; casevo-styled via the
// .casevo-img-delete class in index.css.
// ============================================================
import { Extension } from '@tiptap/core'
import { Plugin, PluginKey, NodeSelection } from '@tiptap/pm/state'

export const ImageActions = Extension.create({
  name: 'imageActions',

  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: new PluginKey('imageActions'),
        view() {
          let button: HTMLButtonElement | null = null

          const removeButton = (): void => {
            if (button) {
              button.remove()
              button = null
            }
          }

          // `pos` is the image node's document position, captured when the ✕ is
          // mounted (below). We delete THAT node rather than trusting the live
          // selection: pressing the button (a DOM element inside the editor)
          // tends to collapse the NodeSelection to a text selection before the
          // handler runs, so a selection-based deleteSelection would silently no-op
          // (the image stays, nothing is committed, onUpdate never fires — the
          // exact "looks deleted but comes back after refresh" bug). Re-selecting
          // the node by pos and deleting dispatches a real transaction → onUpdate
          // → onChange → the parent's HTML loses the image.
          const buildButton = (pos: number): HTMLButtonElement => {
            const el = document.createElement('button')
            el.type = 'button'
            el.className = 'casevo-img-delete'
            el.setAttribute('aria-label', 'Remove image')
            el.title = 'Remove image'
            el.textContent = '✕'
            // mousedown + preventDefault so the button press doesn't blur/scroll;
            // the delete targets the captured pos, so it works regardless of what
            // the selection has become by click-time.
            el.addEventListener('mousedown', (e) => {
              e.preventDefault()
              e.stopPropagation()
              const node = editor.state.doc.nodeAt(pos)
              if (node && node.type.name === 'image') {
                // Re-establish the NodeSelection on the captured image, then delete
                // it. chain().run() dispatches → onUpdate fires → parent value updates.
                editor.chain().setNodeSelection(pos).deleteSelection().run()
              }
            })
            return el
          }

          return {
            update(view) {
              const { selection } = view.state
              const isImage =
                selection instanceof NodeSelection && selection.node.type.name === 'image'
              if (!isImage) {
                removeButton()
                return
              }
              const dom = view.nodeDOM(selection.from) as HTMLElement | null
              if (!dom) {
                removeButton()
                return
              }
              // Prefer the resize wrapper (position:relative) so the ✕ overlays
              // the image corner; fall back to the node's own DOM otherwise.
              const host =
                (dom.querySelector('[data-resize-wrapper]') as HTMLElement | null) ?? dom
              // Already mounted on this host — nothing to do.
              if (button && button.parentElement === host) return
              removeButton()
              if (getComputedStyle(host).position === 'static') {
                host.style.position = 'relative'
              }
              // Capture the selected image's position now; the ✕ deletes THIS node.
              button = buildButton(selection.from)
              host.appendChild(button)
            },
            destroy() {
              removeButton()
            },
          }
        },
      }),
    ]
  },
})

export default ImageActions
